import { apiErrorMessage, type ApiClient } from './apiClient';

// Envelope mechanics only. React-free; baseUrl ends in /v1/ (ApiContext).

interface Envelope<R> {
  result: R;
}

/** A refusal the API stated: `message` is the server's own sentence. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

const codeOf = (raw: string): string | null => {
  const sep = raw.indexOf(' - ');
  if (sep < 0) return null;
  try {
    const parsed = JSON.parse(raw.slice(sep + 3)) as { code?: unknown } | null;
    return typeof parsed?.code === 'string' ? parsed.code : null;
  } catch {
    return null;
  }
};

const stated = (error: unknown): unknown => {
  if (!(error instanceof Error) || error instanceof ApiError) return error;
  const head = /^API call failed: (\d{3})\b/.exec(error.message);
  if (!head) return error;
  const status = Number(head[1]);
  return new ApiError(
    apiErrorMessage(error, `the API refused with ${status}`),
    status,
    codeOf(error.message),
  );
};

export const fetchView = async <R>(api: ApiClient, subUrl: string): Promise<R> => {
  try {
    return (await api.get<Envelope<R>>(subUrl)).result;
  } catch (error) {
    throw stated(error);
  }
};

export const storeAction = async <R>(
  api: ApiClient,
  subUrl: string,
  action: string,
  payload: Record<string, unknown>,
): Promise<R> => {
  try {
    return (await api.post<Envelope<R>>(subUrl, { action, payload })).result;
  } catch (error) {
    throw stated(error);
  }
};
